import create from 'zustand'
import type { IEmberMessage } from './types'

interface EmberMessagesState {
  messages: Record<string, IEmberMessage[]>
}

interface EmberMessagesActions {
  addMessage: (threadId: string, message: IEmberMessage) => void
  addMessages: (threadId: string, messages: IEmberMessage[]) => void
  updateLastMessage: (threadId: string, updates: Partial<IEmberMessage>) => void
  replaceLastMessage: (threadId: string, message: IEmberMessage) => void
  clearMessages: (threadId: string) => void
  removeThinkingMessages: (threadId: string) => void
  handleStreamingUpdate: (threadId: string, textDelta: string) => void
  handleErrorMessage: (threadId: string, displayValue: string) => void
  startNewConversation: (threadId: string, userMessage: IEmberMessage) => void
  completeLastMessage: (threadId: string) => void
  initThread: (threadId: string) => void
}

type EmberMessagesStore = EmberMessagesState & EmberMessagesActions

export const useEmberMessagesStore = create<EmberMessagesStore>((set, get) => ({
  messages: {},
  addMessage: (threadId, message) =>
    set(state => ({
      messages: {
        ...state.messages,
        [threadId]: [...state.messages[threadId], message],
      },
    })),

  addMessages: (threadId, messages) =>
    set(state => ({
      messages: {
        ...state.messages,
        [threadId]: [...state.messages[threadId], ...messages],
      },
    })),

  updateLastMessage: (threadId, updates) =>
    set(state => {
      if (state.messages[threadId].length === 0) return state
      const lastMessage = state.messages[threadId][state.messages[threadId].length - 1]
      const updatedMessage = { ...lastMessage, ...updates }
      return {
        messages: {
          ...state.messages,
          [threadId]: [...state.messages[threadId].slice(0, -1), updatedMessage],
        },
      }
    }),

  replaceLastMessage: (threadId, message) =>
    set(state => ({
      messages: {
        ...state.messages,
        [threadId]: [...state.messages[threadId].slice(0, -1), message],
      },
    })),

  clearMessages: threadId =>
    set(state => ({
      messages: {
        ...state.messages,
        [threadId]: [],
      },
    })),

  removeThinkingMessages: threadId =>
    set(state => ({
      messages: {
        ...state.messages,
        [threadId]: state.messages[threadId].filter(msg => !msg.isThinking),
      },
    })),

  // Specific message operations
  handleStreamingUpdate: (threadId, textDelta) => {
    const { messages, updateLastMessage } = get()
    if (messages[threadId].length === 0) return

    const lastMessage = messages[threadId][messages[threadId].length - 1]
    const displayValue = lastMessage.displayValue ? lastMessage.displayValue + textDelta : textDelta

    updateLastMessage(threadId, { displayValue, isThinking: false })
  },

  handleErrorMessage: (threadId, displayValue) => {
    const { removeThinkingMessages, addMessage } = get()
    const errorMessage: IEmberMessage = {
      role: 'ember',
      displayValue,
    }

    removeThinkingMessages(threadId)
    addMessage(threadId, errorMessage)
  },

  initThread: threadId => {
    const currentMessages = get().messages
    if (!currentMessages[threadId]) {
      set(state => ({
        messages: {
          ...state.messages,
          [threadId]: [],
        },
      }))
    }
  },

  startNewConversation: (threadId, userMessage) => {
    const { removeThinkingMessages, addMessages } = get()

    removeThinkingMessages(threadId)

    const thinkingMessage: IEmberMessage = {
      role: 'ember',
      displayValue: null,
      isThinking: true,
    }

    addMessages(threadId, [userMessage, thinkingMessage])
  },

  completeLastMessage: threadId => {
    const { updateLastMessage } = get()
    updateLastMessage(threadId, { isThinking: false })
  },
}))
